import { STORAGE_KEYS } from './constants.js';
import { apiService } from './api.js';

export class StorageManager {
    constructor() {
        this.initializeStorage();
        this.apiService = apiService;
    }
    
    initializeStorage() {
        // Initialize storage with default values if not present
        if (!this.hasItem(STORAGE_KEYS.HIGH_SCORE)) {
            this.setItem(STORAGE_KEYS.HIGH_SCORE, 0);
        }
        
        if (!this.hasItem(STORAGE_KEYS.LEADERBOARD)) {
            this.setItem(STORAGE_KEYS.LEADERBOARD, []);
        }
        
        if (!this.hasItem(STORAGE_KEYS.SETTINGS)) {
            this.setItem(STORAGE_KEYS.SETTINGS, {
                soundEnabled: true,
                musicEnabled: true,
                vibrationEnabled: true,
                difficulty: 'normal'
            });
        }
    }
    
    // Generic storage methods
    setItem(key, value) {
        try {
            localStorage.setItem(key, JSON.stringify(value));
            return true;
        } catch (error) {
            console.error('Error saving to localStorage:', error);
            return false;
        }
    }
    
    getItem(key, defaultValue = null) {
        try {
            const item = localStorage.getItem(key);
            return item ? JSON.parse(item) : defaultValue;
        } catch (error) {
            console.error('Error reading from localStorage:', error);
            return defaultValue;
        }
    }
    
    hasItem(key) {
        return localStorage.getItem(key) !== null;
    }
    
    removeItem(key) {
        try {
            localStorage.removeItem(key);
            return true;
        } catch (error) {
            console.error('Error removing from localStorage:', error);
            return false;
        }
    }
    
    clear() {
        try {
            // Only clear our game's data
            Object.values(STORAGE_KEYS).forEach(key => {
                localStorage.removeItem(key);
            });
            return true;
        } catch (error) {
            console.error('Error clearing localStorage:', error);
            return false;
        }
    }
    
    // High Score methods
    async getHighScore() {
        // Try to get from server first, fallback to local
        const serverHighScore = await this.apiService.getHighScore();
        const localHighScore = this.getItem(STORAGE_KEYS.HIGH_SCORE, 0);
        return Math.max(serverHighScore, localHighScore);
    }
    
    setHighScore(score) {
        if (typeof score === 'number' && score >= 0) {
            return this.setItem(STORAGE_KEYS.HIGH_SCORE, score);
        }
        return false;
    }
    
    isNewHighScore(score) {
        return score > this.getHighScore();
    }
    
    // Leaderboard methods
    async getLeaderboard(limit = 10) {
        // Try to get from server first, fallback to local
        const serverLeaderboard = await this.apiService.getLeaderboard(limit);
        if (serverLeaderboard && serverLeaderboard.length > 0) {
            return serverLeaderboard;
        }
        
        // Fallback to local leaderboard
        const localLeaderboard = this.getItem(STORAGE_KEYS.LEADERBOARD, []);
        return localLeaderboard.slice(0, limit);
    }
    
    async addScore(name, score, date = new Date(), contactDetails = {}) {
        if (typeof score !== 'number' || score < 0) {
            return false;
        }
        
        // Submit to server (with local fallback)
        const result = await this.apiService.submitScore(name, score, contactDetails);
        
        // Also save locally for backup
        const leaderboard = this.getItem(STORAGE_KEYS.LEADERBOARD, []);
        
        // Create new score entry
        const newEntry = {
            id: this.generateId(),
            name: name.trim() || 'Anonymous',
            score: score,
            date: date.toISOString(),
            timestamp: Date.now()
        };
        
        // Add to local leaderboard
        leaderboard.push(newEntry);
        
        // Sort by score (descending) and then by date (ascending for ties)
        leaderboard.sort((a, b) => {
            if (a.score !== b.score) {
                return b.score - a.score; // Higher score first
            }
            return new Date(a.date) - new Date(b.date); // Earlier date first for ties
        });
        
        // Keep only top 50 scores locally
        const trimmedLeaderboard = leaderboard.slice(0, 50);
        
        this.setItem(STORAGE_KEYS.LEADERBOARD, trimmedLeaderboard);
        
        return result;
    }
    
    removeScore(scoreId) {
        const leaderboard = this.getItem(STORAGE_KEYS.LEADERBOARD, []);
        const filteredLeaderboard = leaderboard.filter(entry => entry.id !== scoreId);
        return this.setItem(STORAGE_KEYS.LEADERBOARD, filteredLeaderboard);
    }
    
    clearLeaderboard() {
        return this.setItem(STORAGE_KEYS.LEADERBOARD, []);
    }
    
    getPlayerStats(playerName) {
        const leaderboard = this.getItem(STORAGE_KEYS.LEADERBOARD, []);
        const playerScores = leaderboard.filter(entry => 
            entry.name.toLowerCase() === playerName.toLowerCase()
        );
        
        if (playerScores.length === 0) {
            return null;
        }
        
        const totalGames = playerScores.length;
        const bestScore = Math.max(...playerScores.map(s => s.score));
        const averageScore = playerScores.reduce((sum, s) => sum + s.score, 0) / totalGames;
        const totalScore = playerScores.reduce((sum, s) => sum + s.score, 0);
        
        return {
            name: playerName,
            totalGames,
            bestScore,
            averageScore: Math.round(averageScore),
            totalScore,
            firstGame: new Date(Math.min(...playerScores.map(s => s.timestamp))),
            lastGame: new Date(Math.max(...playerScores.map(s => s.timestamp)))
        };
    }
    
    // Settings methods
    getSettings() {
        return this.getItem(STORAGE_KEYS.SETTINGS, {
            soundEnabled: true,
            musicEnabled: true,
            vibrationEnabled: true,
            difficulty: 'normal'
        });
    }
    
    updateSettings(newSettings) {
        const currentSettings = this.getSettings();
        const updatedSettings = { ...currentSettings, ...newSettings };
        return this.setItem(STORAGE_KEYS.SETTINGS, updatedSettings);
    }
    
    getSetting(key, defaultValue = null) {
        const settings = this.getSettings();
        return settings.hasOwnProperty(key) ? settings[key] : defaultValue;
    }
    
    setSetting(key, value) {
        return this.updateSettings({ [key]: value });
    }
    
    // Game statistics
    getGameStats() {
        const leaderboard = this.getItem(STORAGE_KEYS.LEADERBOARD, []);
        const highScore = this.getHighScore();
        
        if (leaderboard.length === 0) {
            return {
                totalGames: 0,
                highScore: highScore,
                averageScore: 0,
                totalScore: 0,
                uniquePlayers: 0,
                mostRecentGame: null
            };
        }
        
        const uniquePlayers = new Set(leaderboard.map(entry => entry.name)).size;
        const totalScore = leaderboard.reduce((sum, entry) => sum + entry.score, 0);
        const averageScore = totalScore / leaderboard.length;
        const mostRecentGame = new Date(Math.max(...leaderboard.map(entry => entry.timestamp)));
        
        return {
            totalGames: leaderboard.length,
            highScore: highScore,
            averageScore: Math.round(averageScore),
            totalScore: totalScore,
            uniquePlayers: uniquePlayers,
            mostRecentGame: mostRecentGame
        };
    }
    
    // Export/Import functionality
    exportData() {
        const data = {
            version: '1.0.0',
            exportDate: new Date().toISOString(),
            highScore: this.getHighScore(),
            leaderboard: this.getLeaderboard(50),
            settings: this.getSettings()
        };
        
        return JSON.stringify(data, null, 2);
    }
    
    importData(jsonData) {
        try {
            const data = JSON.parse(jsonData);
            
            if (data.version && data.exportDate) {
                // Validate data structure
                if (typeof data.highScore === 'number' && 
                    Array.isArray(data.leaderboard) && 
                    typeof data.settings === 'object') {
                    
                    this.setItem(STORAGE_KEYS.HIGH_SCORE, data.highScore);
                    this.setItem(STORAGE_KEYS.LEADERBOARD, data.leaderboard);
                    this.setItem(STORAGE_KEYS.SETTINGS, data.settings);
                    
                    return true;
                }
            }
            return false;
        } catch (error) {
            console.error('Error importing data:', error);
            return false;
        }
    }
    
    // Utility methods
    generateId() {
        return Date.now().toString(36) + Math.random().toString(36).substr(2);
    }
    
    isStorageAvailable() {
        try {
            const test = '__storage_test__';
            localStorage.setItem(test, test);
            localStorage.removeItem(test);
            return true;
        } catch (e) {
            return false;
        }
    }
    
    getStorageUsage() {
        if (!this.isStorageAvailable()) {
            return { used: 0, total: 0, percentage: 0 };
        }
        
        let used = 0;
        Object.values(STORAGE_KEYS).forEach(key => {
            const item = localStorage.getItem(key);
            if (item) {
                used += item.length;
            }
        });
        
        // Estimate total storage (most browsers allow ~5-10MB)
        const estimatedTotal = 5 * 1024 * 1024; // 5MB
        const percentage = (used / estimatedTotal) * 100;
        
        return {
            used: used,
            total: estimatedTotal,
            percentage: Math.round(percentage)
        };
    }
}
